// src/features/userprofile/pickerCandidates.ts
// The shape UserPicker offers people in, and the rules for building that shape
// out of the four different things the backend hands us: a full Userprofile, a
// privacy-projected search hit, a project membership record and a team member
// actor. Kept apart from the component because it is the part worth testing —
// the identity rules below are subtle and a mistake in them silently assigns
// work to the wrong person.

import type { Userprofile, UserprofileSearchResult } from '@core/types/userprofile';

/**
 * One pickable person.
 *
 * Two ids, deliberately. `profileId` is what search, chat and membership
 * records key on; `userId` is what the picker's callers store (task assignee,
 * chat member resolution) and is not always known up front — search results are
 * privacy-projected and carry no user reference at all. `resolveUserId` in the
 * component fills it in at pick time, so the value a caller receives never
 * changes meaning depending on which list the person was found in.
 */
export interface PickerCandidate {
  /** Userprofile entity id. Empty only for team actors that carry no profile. */
  profileId: string;
  /** User id when known; resolved on pick otherwise. */
  userId?: string;
  displayName: string;
  /** Second line — @username, e-mail or full name. Never a UUID. */
  secondary: string;
  avatar?: string;
}

/** Stable key for dedupe and React lists. Profile id wins: it is the id every source has. */
export function candidateKey(c: PickerCandidate): string {
  return c.profileId || c.userId || '';
}

/** Does this candidate answer to `id`, whichever of the two ids the caller holds? */
export function candidateHasId(c: PickerCandidate, id: string): boolean {
  if (!id) return false;
  return c.profileId === id || c.userId === id;
}

function fullName(first?: string, last?: string): string {
  return [first, last].filter(Boolean).join(' ');
}

/**
 * Pick the second line. Order is by how much it helps tell two people apart:
 * a handle is unique, a full name disambiguates nicknames, and neither is worth
 * printing when it only repeats the name already on the first line.
 */
function secondaryLine(displayName: string, username?: string, name?: string, email?: string): string {
  if (username && username !== displayName) return `@${username}`;
  if (name && name !== displayName) return name;
  if (email && email !== displayName) return email;
  return '';
}

/** Best human label, falling back through the profile's own fields — never a UUID. */
function profileLabel(displayName: string, name: string, username: string, fallback: string): string {
  return displayName.trim() || name.trim() || username.trim() || fallback;
}

export function candidateFromProfile(p: Userprofile): PickerCandidate {
  const name = fullName(p.first_name, p.last_name);
  const displayName = profileLabel(p.display_name ?? '', name, p.username ?? '', '?');
  return {
    profileId: p.entity?.id ?? '',
    userId: p.user?.id || undefined,
    displayName,
    secondary: secondaryLine(displayName, p.username, name),
    avatar: p.avatar?.icon || p.avatar?.title,
  };
}

export function candidateFromSearchResult(r: UserprofileSearchResult): PickerCandidate {
  const name = fullName(r.first_name, r.last_name);
  const displayName = profileLabel(r.display_name ?? '', name, r.username ?? '', '?');
  return {
    profileId: r.entity?.id ?? '',
    displayName,
    secondary: secondaryLine(displayName, r.username, name),
    avatar: r.avatar?.icon || r.avatar?.title,
  };
}

/**
 * A team member arrives as an Actor: it already carries a name, and its entity
 * id is the id the picker has always emitted for teams. Treated as the user id
 * so that switching the platform-wide list to search does not quietly change
 * what team picks mean.
 */
export function candidateFromTeamActor(actor: {
  entity?: { id?: string };
  display_name?: string;
  avatar?: string;
}, unknownLabel: string): PickerCandidate {
  return {
    profileId: '',
    userId: actor.entity?.id ?? '',
    displayName: actor.display_name?.trim() || unknownLabel,
    secondary: '',
    avatar: actor.avatar,
  };
}

/**
 * Merge lists in priority order, keeping the first occurrence of each person
 * but letting a later, richer copy fill in ids and names the first one lacked.
 * A search hit and a recent pick are the same human; showing them twice, or
 * dropping the user id the recent pick had already resolved, are both bugs.
 */
export function mergeCandidates(...lists: PickerCandidate[][]): PickerCandidate[] {
  const out: PickerCandidate[] = [];
  const byProfile = new Map<string, number>();
  const byUser = new Map<string, number>();

  for (const list of lists) {
    for (const c of list) {
      if (!candidateKey(c)) continue;
      const at = (c.profileId ? byProfile.get(c.profileId) : undefined)
        ?? (c.userId ? byUser.get(c.userId) : undefined);
      if (at === undefined) {
        out.push(c);
        if (c.profileId) byProfile.set(c.profileId, out.length - 1);
        if (c.userId) byUser.set(c.userId, out.length - 1);
        continue;
      }
      const existing = out[at];
      const merged: PickerCandidate = {
        profileId: existing.profileId || c.profileId,
        userId: existing.userId || c.userId,
        displayName: existing.displayName && existing.displayName !== '?' ? existing.displayName : c.displayName,
        secondary: existing.secondary || c.secondary,
        avatar: existing.avatar || c.avatar,
      };
      out[at] = merged;
      if (merged.profileId) byProfile.set(merged.profileId, at);
      if (merged.userId) byUser.set(merged.userId, at);
    }
  }
  return out;
}

/** Local match for lists we already hold in full (project/team members). */
export function matchesQuery(c: PickerCandidate, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return c.displayName.toLowerCase().includes(q) || c.secondary.toLowerCase().includes(q);
}

// --- Recent picks -----------------------------------------------------------
//
// The picker opens on people you have picked before rather than on a page of
// strangers. That set has to survive a reload to be worth anything, and it is
// per-browser preference rather than server state, so it lives in localStorage.

const RECENTS_KEY = 'corde.userPicker.recent';
export const RECENTS_LIMIT = 8;

export function readRecentPicks(storage?: Pick<Storage, 'getItem' | 'setItem'>): PickerCandidate[] {
  const store = storage ?? safeStorage();
  if (!store) return [];
  try {
    const raw = store.getItem(RECENTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((c): c is PickerCandidate =>
        !!c && typeof c === 'object' && typeof c.displayName === 'string' && !!(c.profileId || c.userId))
      .slice(0, RECENTS_LIMIT);
  } catch {
    // A corrupted or unreadable entry is not worth an error path — the picker
    // simply opens on its "start typing" hint instead.
    return [];
  }
}

export function rememberPick(
  candidate: PickerCandidate,
  storage?: Pick<Storage, 'getItem' | 'setItem'>,
): PickerCandidate[] {
  const store = storage ?? safeStorage();
  const next = mergeCandidates([candidate], readRecentPicks(store ?? undefined)).slice(0, RECENTS_LIMIT);
  if (store) {
    try {
      store.setItem(RECENTS_KEY, JSON.stringify(next));
    } catch {
      // Private mode / quota. Recents degrade to session-only; nothing else breaks.
    }
  }
  return next;
}

function safeStorage(): Pick<Storage, 'getItem' | 'setItem'> | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}
